// Seed of the in-memory Work twin (M2-07): spaces, boards with their statuses, fields and saved
// views, tasks with checklists, subtasks, dependencies and threads, and the owner's inbox.
// Acme is a company whose recruiting runs on two digital employees (Riley, the
// recruiting coordinator, and Sam, the sourcer) under Jordan Lee's "Recruiting lead" seat; its
// work reads like that. Dates are relative to "today" so overdue, due-soon and the calendar and
// timeline views always have something in range. Plain data: fake-work.ts owns the rules.
import { categoryOf } from "@companyos/domain-work/chat";
import type { OperationOutput } from "@companyos/sdk";
import type { FakeCompany, FakeDe } from "./fake-data";
import { reportingDutyId } from "./fake-reporting";
import { seedTaskTagIds } from "./fake-work-bundle";

export type Space = OperationOutput<"spaces.get">;
export type Board = OperationOutput<"boards.get">;
export type View = OperationOutput<"views.list">["data"][number];
export type FieldDef = OperationOutput<"fields.list">["data"][number];
export type Channel = OperationOutput<"spaces.sidebar">["channels"][number];
export type InboxItem = OperationOutput<"inbox.list">["data"][number];
export type StatusDef = Board["status_set"][number];
export type ViewFilter = View["filter"];

/** A task as the twin stores it: the row plus what the card and the detail join in. */
export type StoredTask = Omit<
  OperationOutput<"tasks.get">,
  | "assignee_seat_ids"
  | "fields"
  | "checklist"
  | "blocked"
  | "checklist_items"
  | "checklists"
  | "dependencies"
  | "depends_on_task_ids"
  | "field_values"
  | "assignees"
> & {
  assignee_seat_ids: string[];
  /** field_def id → value. */
  fields: Record<string, unknown>;
  /** DOCS-KB: the task's named checklists; every item names one by `checklist_id`. */
  checklists: OperationOutput<"tasks.get">["checklists"];
  checklist_items: OperationOutput<"tasks.get">["checklist_items"];
  dependencies: OperationOutput<"tasks.get">["dependencies"];
  deleted?: boolean;
};

/** A seeded message on a task's thread: who said it, how long ago, and the text. */
export type SeedMessage = { author: string; minutesAgo: number; text: string; mentions?: string[] };

export type WorkSeed = {
  spaces: Space[];
  channels: Channel[];
  boards: Board[];
  fields: FieldDef[];
  views: View[];
  tasks: StoredTask[];
  /** Task id → messages on its thread, oldest first. */
  messages: Map<string, SeedMessage[]>;
  inbox: InboxItem[];
};

const DAY = 86_400_000;

type Ids = { next: (group: number) => string };

/** Generic statuses (To do → In progress → In review → Done). */
export const BASIC_STATUSES: StatusDef[] = [
  { key: "todo", name: "To do", done: false, color: "neutral" },
  { key: "doing", name: "In progress", done: false, started: true, color: "accent" },
  { key: "review", name: "In review", done: false, started: true, color: "warning" },
  { key: "done", name: "Done", done: true, color: "success" },
];

const PIPELINE_STATUSES: StatusDef[] = [
  { key: "sourcing", name: "Sourcing", done: false, color: "neutral" },
  { key: "screening", name: "Screening", done: false, started: true, color: "info" },
  { key: "interview", name: "Interview", done: false, started: true, color: "accent" },
  { key: "offer", name: "Offer", done: false, started: true, color: "warning" },
  { key: "hired", name: "Hired", done: true, color: "success" },
  { key: "closed", name: "Closed", done: true, color: "danger" },
];

const ONBOARDING_STATUSES: StatusDef[] = [
  { key: "todo", name: "To do", done: false, color: "neutral" },
  { key: "doing", name: "In progress", done: false, started: true, color: "accent" },
  { key: "waiting", name: "Waiting on client", done: false, color: "warning" },
  { key: "done", name: "Done", done: true, color: "success" },
];

/** A seeded checklist item: text, done, the assignee's seat key, due in days. */
type SeedItem = [string, boolean, (string | undefined)?, (number | undefined)?];

type TaskSpec = {
  key: string;
  title: string;
  status: string;
  priority?: number;
  /** Days from today (negative = past). */
  due?: number;
  start?: number;
  /** Seat keys: `lead` (the human seat), or a DE's seat by DE name in lower case. */
  assignees?: string[];
  checklist?: [string, boolean][];
  /** DOCS-KB: named checklists; an item is [text, done, assignee seat key?, due in days?]. */
  checklists?: { title: string; items: SeedItem[] }[];
  fields?: Record<string, unknown>;
  parent?: string;
  /** Keys this task depends on: `!key` is soft, `key` is hard. */
  depends?: string[];
  messages?: SeedMessage[];
  createdDaysAgo?: number;
};

type BoardSpec = {
  key: string;
  name: string;
  statuses: StatusDef[];
  fields?: { key: string; name: string; type: FieldDef["type"]; choices?: string[] }[];
  views: {
    kind: View["kind"];
    name: string;
    group_by?: string | null;
    sort?: View["sort"];
    filter?: ViewFilter;
    visible_fields?: string[];
    /** CAL-WORK-LAYERS: a calendar's date, a field key here (`field:{key}`) or a built-in. */
    date_field?: string;
  }[];
  tasks: TaskSpec[];
};

type SpaceSpec = {
  name: string;
  kind: Space["kind"];
  icon: string;
  description: string;
  channels: string[];
  boards: BoardSpec[];
};

const key = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .replace(/^(\d)/, "c_$1");

/** Acme's work: recruiting run by Riley and Sam, a client onboarding, and the office. */
function acmeSpaces(): SpaceSpec[] {
  return [
    {
      name: "Recruiting",
      kind: "team",
      icon: "users",
      description:
        "Hiring for Acme's open roles: sourcing and screening by Riley and Sam, interviews and offers with Jordan.",
      channels: ["recruiting", "hiring-pipeline"],
      boards: [
        {
          key: "pipeline",
          name: "Hiring pipeline",
          statuses: PIPELINE_STATUSES,
          fields: [
            {
              key: "role",
              name: "Role",
              type: "select",
              choices: ["Backend engineer", "Account executive", "Office manager"],
            },
            {
              key: "source",
              name: "Source",
              type: "select",
              choices: ["LinkedIn", "Referral", "Job board"],
            },
            { key: "salary", name: "Salary band", type: "text" },
          ],
          views: [
            { kind: "board", name: "Pipeline", group_by: "status" },
            {
              kind: "list",
              name: "By role",
              group_by: "field:role",
              sort: [{ field: "due_at", direction: "asc" }],
            },
            { kind: "table", name: "All candidates", group_by: null },
            { kind: "calendar", name: "Interviews", group_by: null },
            { kind: "timeline", name: "Timeline", group_by: null },
            { kind: "gallery", name: "Cards", group_by: null },
          ],
          tasks: [
            {
              key: "screen_backend",
              title: "Screen 12 backend applicants from LinkedIn",
              status: "screening",
              priority: 2,
              start: -3,
              due: 1,
              assignees: ["lead", "riley"],
              fields: { role: "backend_engineer", source: "linkedin" },
              checklist: [
                ["Shortlist against the must-haves", true],
                ["Check work permits", false],
                ["Send the top four to Jordan", false],
              ],
              messages: [
                {
                  author: "riley",
                  minutesAgo: 190,
                  text: "Shortlisted 4 of 12. Two need a work-permit check before they go to Jordan.",
                },
                {
                  author: "lead",
                  minutesAgo: 75,
                  text: "Thanks. Send me the two with permits today, the others once checked.",
                },
                {
                  author: "riley",
                  minutesAgo: 20,
                  text: "Done: Priya N. and Tomás R. are in your review column. @Maya the salary band for Tomás is above the range.",
                  mentions: ["owner"],
                },
              ],
            },
            {
              key: "priya_panel",
              title: "Panel interview: Priya N. (Backend engineer)",
              status: "interview",
              priority: 1,
              start: 0,
              due: 2,
              assignees: ["lead"],
              fields: { role: "backend_engineer", source: "referral", salary: "€70–80k" },
              depends: ["screen_backend"],
              checklists: [
                {
                  title: "Before the panel",
                  items: [
                    ["Book the panel (CTO, team lead)", true, "lead"],
                    ["Send the take-home brief", true],
                    ["Share the CV with the panel", false, "lead", 1],
                  ],
                },
                {
                  title: "After the panel",
                  items: [
                    ["Collect panel scores", false, "lead", 2],
                    ["Write the debrief", false, undefined, -1],
                    ["Decide go or no-go", false],
                  ],
                },
              ],
            },
            {
              key: "tomas_offer",
              title: "Offer letter for Tomás R.",
              status: "offer",
              priority: 2,
              due: -1,
              assignees: ["lead", "riley"],
              fields: { role: "backend_engineer", source: "linkedin", salary: "€82k" },
              messages: [
                {
                  author: "riley",
                  minutesAgo: 1500,
                  text: "Draft offer is attached to the page body. Waiting for the salary approval.",
                },
              ],
            },
            {
              key: "ae_sourcing",
              title: "Source 30 account executives in Berlin",
              status: "sourcing",
              priority: 3,
              start: -1,
              due: 5,
              assignees: ["lead", "sam"],
              fields: { role: "account_executive", source: "linkedin" },
              checklist: [
                ["Boolean search on LinkedIn Recruiter", true],
                ["Referral ask in #general", false],
                ["Score the first 30", false],
              ],
            },
            {
              key: "ae_outreach",
              title: "Personalised outreach to the top 15 AEs",
              status: "sourcing",
              priority: 3,
              start: 4,
              due: 8,
              assignees: ["sam"],
              fields: { role: "account_executive", source: "linkedin" },
              depends: ["ae_sourcing"],
            },
            {
              key: "office_mgr",
              title: "Screen office manager referrals",
              status: "screening",
              priority: 4,
              start: -2,
              due: 3,
              assignees: ["riley"],
              fields: { role: "office_manager", source: "referral" },
            },
            {
              key: "lena_hired",
              title: "Lena K. accepted: start date 3 Nov",
              status: "hired",
              priority: 0,
              due: -6,
              start: -12,
              assignees: ["lead"],
              fields: { role: "account_executive", source: "job_board" },
              createdDaysAgo: 20,
            },
            {
              key: "marco_closed",
              title: "Marco D. withdrew after the panel",
              status: "closed",
              priority: 0,
              due: -9,
              assignees: ["riley"],
              fields: { role: "backend_engineer", source: "job_board" },
              createdDaysAgo: 18,
            },
            {
              key: "refs_priya",
              title: "Reference checks for Priya N.",
              status: "sourcing",
              priority: 3,
              due: 6,
              assignees: ["riley"],
              parent: "priya_panel",
              depends: ["!priya_panel"],
            },
          ],
        },
        {
          key: "ops",
          name: "Recruiting ops",
          statuses: BASIC_STATUSES,
          fields: [{ key: "effort", name: "Effort (hours)", type: "number" }],
          views: [
            { kind: "board", name: "Board", group_by: "status" },
            { kind: "list", name: "By owner", group_by: "assignee" },
            { kind: "calendar", name: "Calendar", group_by: null },
          ],
          tasks: [
            {
              key: "friday_report",
              title: "Friday report for the Acme leadership",
              status: "doing",
              priority: 2,
              due: 3,
              start: 0,
              assignees: ["lead", "riley"],
              fields: { effort: 2 },
              checklist: [
                ["Pull the week's pipeline numbers", true],
                ["Write the supervisor note", false],
              ],
            },
            {
              key: "job_board",
              title: "Renew the job board subscription",
              status: "todo",
              priority: 1,
              due: -2,
              assignees: ["lead"],
              fields: { effort: 0.5 },
            },
            {
              key: "screening_sop",
              title: "Update the screening SOP with the permit check",
              status: "review",
              priority: 3,
              due: 4,
              assignees: ["riley"],
              depends: ["!screen_backend"],
            },
            {
              key: "ats_tags",
              title: "Clean up candidate tags in the ATS",
              status: "todo",
              priority: 4,
              due: 10,
              assignees: ["sam"],
              fields: { effort: 3 },
            },
            {
              key: "interview_kit",
              title: "Interview kit for account executives",
              status: "done",
              priority: 3,
              due: -4,
              assignees: ["lead"],
              createdDaysAgo: 14,
            },
          ],
        },
      ],
    },
    {
      name: "Client: Northwind",
      kind: "client",
      icon: "building",
      description:
        "Onboarding Northwind Traders as a recruiting client: contract, intake and the first two roles.",
      channels: ["client-northwind"],
      boards: [
        {
          key: "onboarding",
          name: "Onboarding",
          statuses: ONBOARDING_STATUSES,
          views: [
            { kind: "list", name: "Checklist", group_by: "status" },
            { kind: "board", name: "Board", group_by: "status" },
          ],
          tasks: [
            {
              key: "nw_sow",
              title: "Sign the Northwind statement of work",
              status: "waiting",
              priority: 1,
              due: 1,
              assignees: ["lead"],
              checklist: [
                ["Send the SOW", true],
                ["Countersignature", false],
              ],
            },
            {
              key: "nw_intake",
              title: "Intake call: roles, must-haves, salary bands",
              status: "doing",
              priority: 2,
              due: 2,
              start: 1,
              assignees: ["lead", "riley"],
              depends: ["nw_sow"],
            },
            {
              key: "nw_portal",
              title: "Set up the client portal and the Friday report",
              status: "todo",
              priority: 3,
              due: 7,
              assignees: ["riley"],
              depends: ["nw_intake"],
            },
            {
              key: "nw_kickoff",
              title: "Kick-off email to the hiring managers",
              status: "done",
              priority: 3,
              due: -3,
              assignees: ["sam"],
              createdDaysAgo: 9,
            },
          ],
        },
      ],
    },
    {
      name: "Operations",
      kind: "project",
      icon: "briefcase",
      description: "Office, tools and admin that keep the team running.",
      channels: ["ops"],
      boards: [
        {
          key: "admin",
          name: "Office & admin",
          statuses: BASIC_STATUSES,
          views: [{ kind: "board", name: "Board", group_by: "status" }],
          tasks: [
            {
              key: "laptops",
              title: "Order laptops for the November starters",
              status: "todo",
              priority: 2,
              due: 9,
              assignees: ["lead"],
            },
            {
              key: "payroll",
              title: "Send October hours to payroll",
              status: "doing",
              priority: 2,
              due: 0,
              assignees: ["lead"],
            },
          ],
        },
        // CAL-WORK-LAYERS: the posts and their publish dates; the "Publish dates" view is the
        // social calendar the owner published into the Calendar (fake-calendar.ts seed).
        {
          key: "social",
          name: "Social media",
          statuses: SOCIAL_STATUSES,
          fields: [
            { key: "publish", name: "Publish date", type: "date" },
            {
              key: "channel",
              name: "Channel",
              type: "select",
              choices: ["LinkedIn", "Instagram", "YouTube"],
            },
          ],
          views: [
            { kind: "board", name: "Posts", group_by: "status" },
            {
              kind: "calendar",
              name: "Publish dates",
              group_by: null,
              date_field: "field:publish",
            },
          ],
          tasks: [
            {
              key: "post_hiring",
              title: "We're hiring: backend engineer",
              status: "live",
              fields: { publish: -3, channel: "linkedin" },
            },
            {
              key: "post_reel",
              title: "Office tour reel",
              status: "filming",
              fields: { publish: 2, channel: "instagram" },
            },
            {
              key: "post_case",
              title: "Northwind case study",
              status: "scheduled",
              fields: { publish: 5, channel: "linkedin" },
            },
            {
              key: "post_video",
              title: "Founder Q&A video",
              status: "idea",
              fields: { publish: 9, channel: "youtube" },
            },
          ],
        },
      ],
    },
  ];
}

/** CAL-WORK-LAYERS: a social media board's stages. */
const SOCIAL_STATUSES: StatusDef[] = [
  { key: "idea", name: "Idea", done: false, color: "neutral" },
  { key: "filming", name: "Filming", done: false, started: true, color: "accent" },
  { key: "scheduled", name: "Scheduled", done: false, started: true, color: "warning" },
  { key: "live", name: "Live", done: true, color: "success" },
];

/** A small space for the other companies, so their Work mode is not empty. */
function smallSpaces(company: FakeCompany): SpaceSpec[] {
  return [
    {
      name: "Team",
      kind: "team",
      icon: "users",
      description: `${company.name}'s shared work.`,
      channels: ["general"],
      boards: [
        {
          key: "tasks",
          name: "Tasks",
          statuses: BASIC_STATUSES,
          views: [
            { kind: "board", name: "Board", group_by: "status" },
            { kind: "list", name: "List", group_by: "status" },
          ],
          tasks: [
            {
              key: "intro",
              title: "Write the hiring brief for the next role",
              status: "doing",
              priority: 2,
              due: 2,
              assignees: ["lead"],
            },
            {
              key: "followup",
              title: "Follow up with last week's candidates",
              status: "todo",
              priority: 3,
              due: 5,
              assignees: ["lead", "first_de"],
            },
          ],
        },
      ],
    },
  ];
}

/**
 * FOUNDER-OS (12 §What lives in it 1): the Founder OS company's Thinking space — the decision log
 * (decision cards: context, options, decision, why) and the ideas being shaped.
 */
const DECISION_STATUSES: StatusDef[] = [
  { key: "open", name: "Open", done: false, color: "neutral" },
  { key: "weighing", name: "Weighing", done: false, started: true, color: "warning" },
  { key: "decided", name: "Decided", done: true, color: "success" },
];

function thinkingSpaces(): SpaceSpec[] {
  return [
    {
      name: "Thinking",
      kind: "project",
      icon: "lightbulb",
      description: "Journal, ideas, strategy pages and the decision log — the founder's own.",
      channels: [],
      boards: [
        {
          key: "decisions",
          name: "Decision log",
          statuses: DECISION_STATUSES,
          views: [
            { kind: "board", name: "Board", group_by: "status" },
            { kind: "list", name: "List", group_by: "status" },
          ],
          tasks: [
            {
              key: "pricing",
              title: "Decide: Founder OS pricing for HTVA's clients",
              status: "weighing",
              priority: 1,
              due: 6,
            },
            {
              key: "acme-coo",
              title: "Decide: hire a COO for Acme or fix delivery first",
              status: "open",
              priority: 1,
              due: 10,
            },
            {
              key: "beta-dues",
              title: "Decided: Beta keeps its own finance stack until Q1",
              status: "decided",
              priority: 3,
            },
          ],
        },
        {
          key: "ideas",
          name: "Ideas",
          statuses: BASIC_STATUSES,
          views: [{ kind: "list", name: "List", group_by: "status" }],
          tasks: [
            {
              key: "shared-cfo",
              title: "A fractional CFO seat shared by Acme and Beta",
              status: "todo",
            },
            { key: "playbook", title: "One hiring playbook across the portfolio", status: "doing" },
          ],
        },
      ],
    },
  ];
}

const iso = (now: Date, days: number) => new Date(now.getTime() + days * DAY).toISOString();
/**
 * A due date: local noon on the day (the screens read due dates as local days, so a UTC hour put
 * "yesterday" on today's local date in an American evening and nothing read overdue).
 */
const dueOn = (now: Date, days: number) => {
  const d = new Date(now.getTime() + days * DAY);
  d.setHours(12, 0, 0, 0);
  return d.toISOString();
};
const startOn = (now: Date, days: number) => {
  const d = new Date(now.getTime() + days * DAY);
  d.setUTCHours(9, 0, 0, 0);
  return d.toISOString();
};

export function seedWork(
  company: FakeCompany,
  des: FakeDe[],
  ids: Ids,
  now: Date,
  /** The company owner's membership, for the inbox and the thread mentions (null: none). */
  ownerMembership: string | null,
): WorkSeed {
  const seed: WorkSeed = {
    spaces: [],
    channels: [],
    boards: [],
    fields: [],
    views: [],
    tasks: [],
    messages: new Map(),
    inbox: [],
  };
  const at = iso(now, -30);
  const sys = { actor_kind: "system" as const, actor_membership_id: null };
  const lead = des[0]?.supervisor.seat_id;
  const seatOf = (k: string): string[] => {
    if (k === "lead") return lead ? [lead] : [];
    if (k === "first_de") return des[0] ? [des[0].seat_id] : [];
    const de = des.find((d) => d.display_name.toLowerCase() === k);
    return de ? [de.seat_id] : [];
  };
  const specs =
    company.slug === "acme"
      ? acmeSpaces()
      : company.kind === "portfolio"
        ? thinkingSpaces()
        : smallSpaces(company);
  const byKey = new Map<string, StoredTask>();
  const pending: { task: StoredTask; spec: TaskSpec }[] = [];
  let number = 0;

  // BOARD-ORDER: the demo boards' tints, varied along each space (the seed-demo's choice).
  const COLORS = ["blue", "green", "purple", "orange", "amber", "rose"] as const;
  for (const [si, s] of specs.entries()) {
    const space: Space = {
      company_id: company.id,
      id: ids.next(0x5100),
      name: s.name,
      kind: s.kind,
      team_id: null,
      owner_membership_id: null,
      icon: s.icon,
      description: s.description,
      is_private: false,
      // MEMBER-UX (migration 0044): shared spaces let members edit; a team's space is its team's.
      settings: s.kind === "team" ? {} : { member_access: "editor" },
      position: si,
      archived_at: null,
      ...sys,
      created_at: at,
      updated_at: at,
    };
    seed.spaces.push(space);
    for (const name of s.channels)
      seed.channels.push(
        channelRow(company.id, ids.next(0x5b00), space.id, name, "space", null, at),
      );
    for (const [bi, b] of s.boards.entries()) {
      const boardId = ids.next(0x5200);
      const home = channelRow(
        company.id,
        ids.next(0x5b00),
        space.id,
        `${key(b.name)}-home`.replaceAll("_", "-"),
        "board_home",
        boardId,
        at,
      );
      seed.channels.push(home);
      const fieldIds = new Map<string, string>();
      for (const [i, f] of (b.fields ?? []).entries()) {
        const id = ids.next(0x5400);
        fieldIds.set(f.key, id);
        seed.fields.push({
          company_id: company.id,
          id,
          board_id: boardId,
          name: f.name,
          type: f.type,
          options: f.choices ? { choices: f.choices.map((c) => ({ key: key(c), name: c })) } : {},
          audience: "both",
          requires_approval: false,
          position: i,
          ...sys,
          created_at: at,
          updated_at: at,
        });
      }
      const viewIds: string[] = [];
      for (const [i, v] of b.views.entries()) {
        const id = ids.next(0x5300);
        viewIds.push(id);
        const groupBy =
          v.group_by?.startsWith("field:") === true
            ? `field:${fieldIds.get(v.group_by.slice(6)) ?? ""}`
            : (v.group_by ?? null);
        seed.views.push({
          company_id: company.id,
          id,
          board_id: boardId,
          kind: v.kind,
          name: v.name,
          filter: v.filter ?? {},
          sort: v.sort ?? [],
          group_by: groupBy,
          visible_fields: v.visible_fields ?? [],
          date_field: v.date_field?.startsWith("field:")
            ? `field:${fieldIds.get(v.date_field.slice(6)) ?? ""}`
            : (v.date_field ?? null),
          card_fields: null,
          is_default: i === 0,
          position: i,
          ...sys,
          created_at: at,
          updated_at: at,
        });
      }
      seed.boards.push({
        company_id: company.id,
        id: boardId,
        space_id: space.id,
        name: b.name,
        home_channel_id: home.id,
        status_set: b.statuses,
        settings: { live_cards: true, ...(viewIds[0] ? { default_view_id: viewIds[0] } : {}) },
        digest_interval: "daily",
        position: bi,
        color: COLORS[(si * 2 + bi) % COLORS.length] ?? null,
        // A space's first board is led by the person on its first assigned task; the others
        // name no lead yet (Work home offers "+ Lead").
        lead_seat_id:
          bi === 0
            ? (seatOf(b.tasks.find((t) => t.assignees?.length)?.assignees?.[0] ?? "")[0] ?? null)
            : null,
        page_id: null,
        archived_at: null,
        version: 1,
        ...sys,
        created_at: at,
        updated_at: at,
      });
      for (const t of b.tasks) {
        number += 1;
        const id = ids.next(0x5500);
        const created = iso(now, -(t.createdDaysAgo ?? 7 - (number % 5)));
        const status = b.statuses.find((x) => x.key === t.status);
        const fields: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(t.fields ?? {})) {
          const fid = fieldIds.get(k);
          // A date field's seed value is days from today (a calendar day, YYYY-MM-DD).
          const dated = (b.fields ?? []).find((f) => f.key === k)?.type === "date";
          if (fid)
            fields[fid] =
              dated && typeof v === "number" ? dueOn(now, v).slice(0, 10) : (v as unknown);
        }
        const task: StoredTask = {
          tag_ids: seedTaskTagIds(company.id, t.title),
          company_id: company.id,
          id,
          board_id: boardId,
          number,
          title: t.title,
          status: t.status,
          priority: t.priority ?? 0,
          parent_task_id: null,
          page_id: null,
          thread_id: t.messages ? ids.next(0x5700) : null,
          due_at: t.due === undefined ? null : dueOn(now, t.due),
          start_at: t.start === undefined ? null : startOn(now, t.start),
          estimate_minutes: null,
          created_by_seat_id: lead ?? null,
          completed_at: status?.done ? (t.due === undefined ? created : dueOn(now, t.due)) : null,
          origin: "manual",
          origin_ref: null,
          client_id: null,
          restricted: false,
          version: 1,
          ...sys,
          created_at: created,
          updated_at: created,
          assignee_seat_ids: (t.assignees ?? []).flatMap(seatOf),
          fields,
          checklists: [],
          checklist_items: [],
          dependencies: [],
        };
        // A flat `checklist` is the task's one "Checklist" (DOCS-KB decision 8).
        const lists =
          t.checklists ??
          (t.checklist?.length ? [{ title: "Checklist", items: t.checklist as SeedItem[] }] : []);
        lists.forEach((l, li) => {
          const checklistId = ids.next(0x5610);
          task.checklists.push({
            company_id: company.id,
            id: checklistId,
            task_id: id,
            title: l.title,
            position: li,
            ...sys,
            created_at: created,
            updated_at: created,
          });
          l.items.forEach(([text, done, who, dueIn], i) => {
            task.checklist_items.push({
              company_id: company.id,
              id: ids.next(0x5600),
              task_id: id,
              checklist_id: checklistId,
              text,
              done_at: done ? created : null,
              done_by_seat_id: done ? (lead ?? null) : null,
              assignee_seat_id: who ? (seatOf(who)[0] ?? null) : null,
              due_at: dueIn === undefined ? null : dueOn(now, dueIn),
              position: i,
              ...sys,
              created_at: created,
              updated_at: created,
            });
          });
        });
        byKey.set(t.key, task);
        pending.push({ task, spec: t });
        seed.tasks.push(task);
        if (t.messages) seed.messages.set(id, t.messages);
      }
    }
  }
  // Parents and dependencies once every task has its id.
  for (const { task, spec } of pending) {
    if (spec.parent) task.parent_task_id = byKey.get(spec.parent)?.id ?? null;
    for (const d of spec.depends ?? []) {
      const soft = d.startsWith("!");
      const on = byKey.get(soft ? d.slice(1) : d);
      if (on)
        task.dependencies.push({
          company_id: company.id,
          task_id: task.id,
          depends_on_task_id: on.id,
          kind: soft ? "soft" : "hard",
          ...sys,
          created_at: task.created_at,
        });
    }
  }
  if (ownerMembership && company.slug === "acme")
    seed.inbox = acmeInbox(company, ids, now, ownerMembership, byKey);
  return seed;
}

function channelRow(
  companyId: string,
  id: string,
  spaceId: string,
  name: string,
  kind: Channel["kind"],
  boardId: string | null,
  at: string,
): Channel {
  return {
    company_id: companyId,
    id,
    space_id: spaceId,
    kind,
    name,
    description: null,
    board_id: boardId,
    team_id: null,
    is_private: false,
    is_agent_room: false,
    is_default: false,
    posting_policy: "everyone",
    topic_policy: "everyone",
    broadcast_policy: "everyone",
    default_notify_level: "all",
    taken_over_by_membership_id: null,
    taken_over_at: null,
    dm_key: null,
    archived_at: null,
    joined: true,
    notify_level: "all",
    muted: false,
    following: false,
    // WF-FIX (2026-10-26): joined, everyone posts: the caller may add members.
    can_invite: true,
    can_manage: false,
    closed: false,
    actor_kind: "system",
    actor_membership_id: null,
    created_at: at,
    updated_at: at,
    unread_count: 0,
    last_message_at: null,
  };
}

/** The owner's inbox: a mention, coalesced task changes, a due reminder, an agent escalation
 * and an older item already done (06 §Inbox). */
function acmeInbox(
  company: FakeCompany,
  ids: Ids,
  now: Date,
  membership: string,
  tasks: Map<string, StoredTask>,
): InboxItem[] {
  const item = (
    minutesAgo: number,
    kind: InboxItem["kind"],
    task: string,
    summary: string,
    extra: Partial<InboxItem> = {},
  ): InboxItem | null => {
    const t = tasks.get(task);
    if (!t) return null;
    const at = new Date(now.getTime() - minutesAgo * 60_000).toISOString();
    return {
      company_id: company.id,
      id: ids.next(0x5900),
      membership_id: membership,
      kind,
      subject_kind: "task",
      subject_id: t.id,
      message_id: null,
      source_event_id: ids.next(0x5c00),
      coalesce_key: kind === "task_change" ? `task:${t.id}` : null,
      count: 1,
      summary,
      read_at: null,
      done_at: null,
      snoozed_until: null,
      desktop: false,
      // INBOX-PREFS: fake-work.ts names who an item is from (the mention's author, the DE).
      category: categoryOf(kind, extra.subject_kind ?? "task"),
      from_membership_id: null,
      nudged_at: null,
      escalated_at: null,
      created_at: at,
      updated_at: at,
      ...extra,
    };
  };
  return [
    // fake-work.ts points `message_id` at the thread message that mentions the owner.
    item(
      20,
      "mention",
      "screen_backend",
      "Riley mentioned you: the salary band for Tomás is above the range.",
    ),
    item(
      45,
      "agent_escalation",
      "tomas_offer",
      "Riley needs you: the offer for Tomás R. waits for a salary approval.",
    ),
    item(
      95,
      "task_change",
      "priya_panel",
      "Panel interview: Priya N. moved to Interview and was rescheduled",
      {
        count: 3,
      },
    ),
    item(180, "reminder", "nw_sow", "Due tomorrow: Sign the Northwind statement of work"),
    // AGENT-REPORTING: the reporting.remind job's late item (subject: the duty).
    item(
      240,
      "reminder",
      "nw_sow",
      "Late report: Interview debriefs logged (Recruiting lead) was due Monday 10:00",
      { subject_kind: "reporting_duty", subject_id: reportingDutyId(3), coalesce_key: null },
    ),
    item(60 * 26, "task_change", "job_board", "Renew the job board subscription is overdue", {
      read_at: new Date(now.getTime() - 60 * 60_000 * 20).toISOString(),
    }),
    item(
      60 * 50,
      "task_change",
      "interview_kit",
      "Interview kit for account executives moved to Done",
      {
        read_at: new Date(now.getTime() - 60 * 60_000 * 48).toISOString(),
        done_at: new Date(now.getTime() - 60 * 60_000 * 48).toISOString(),
      },
    ),
    // COS-MODES: Team · Tell me: the Chief of Staff handed work to an agent; Take back on the row.
    item(
      150,
      "agent_directed",
      "ae_sourcing",
      "Chief of Staff handed Source 30 account executives in Berlin to Sam",
    ),
  ].filter((x): x is InboxItem => x !== null);
}
